import { graphql } from "babel-plugin-relay/macro";
import { Card } from "primereact/card";
import React from "react";
import { useFragment, useMutation } from "react-relay";
import { StartContentConfigEditor_RemoveStartTreeConfigMutation } from "../../../../../../../__generated__/StartContentConfigEditor_RemoveStartTreeConfigMutation.graphql";
import {
	StartContentConfigEditor_TreeNodeFragment$key,
	StartContentConfigType,
} from "../../../../../../../__generated__/StartContentConfigEditor_TreeNodeFragment.graphql";
import {
	ExtendStartContentConfigs,
	ProjectExtendStartContentConfigEditDialogs,
} from "../../../../../../../lms-admin-impl/components/relay/ProjectExtendStartContentConfigEditDialogs";
import { formatDateTimeAsCalendarDate } from "../../../../../core/datetime/DateTimeDisplay";
import { Config, ConfigurationGraphqlInterfaceWithDateTime } from "../components/Config.interfaces";
import { ConfigDropdown, ConfigDropdownOptions } from "../components/ConfigDropdown";
import { ConfigItem } from "../components/ConfigItem";
import { ConfigPreview } from "../components/ConfigPreview";

const TREE_NODE_FRAGMENT = graphql`
	fragment StartContentConfigEditor_TreeNodeFragment on TreeNode {
		id
		typeDefinition {
			... on ELearningContentTypeDefinition {
				startConfigs {
					id
					configType
					... on SpecifiedUserStatusesCanStartContentConfig {
						dateTime
					}
					...ProjectExtendStartContentConfigEditDialogs_StartContentConfigFragment
				}
			}
		}
		...ProjectExtendStartContentConfigEditDialogs_TreeNodeFragment
	}
`;

const REMOVE_START_CONTENT_CONDITION_CONFIGURATION_MUTATION = graphql`
	mutation StartContentConfigEditor_RemoveStartTreeConfigMutation(
		$input: RemoveStartContentConfigInput!
	) {
		Admin {
			Tree {
				removeStartContentConfig(input: $input) {
					contentNode {
						...StartContentConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

interface Props {
	treeNodeFragmentRef: StartContentConfigEditor_TreeNodeFragment$key;
}

export const StartContentConfigEditor = ({ treeNodeFragmentRef }: Props) => {
	const treeNode = useFragment<StartContentConfigEditor_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);

	const [removeStartContentConfig, isRemovingStartContentConfig] =
		useMutation<StartContentConfigEditor_RemoveStartTreeConfigMutation>(
			REMOVE_START_CONTENT_CONDITION_CONFIGURATION_MUTATION,
		);

	const configs: Config<StartContentConfigType, any | never>[] = [...ExtendStartContentConfigs];

	const options: ConfigDropdownOptions<StartContentConfigType>[] = configs.map((c) => {
		return {
			label: c.configKey,
			value: c.configKey,
		};
	});

	const createPreviewOptions = React.useCallback(
		(configuration: ConfigurationGraphqlInterfaceWithDateTime<StartContentConfigType>) => {
			const dateTime =
				configuration.configType === "StartContent_SpecificUserStatusesCan"
					? configuration.dateTime
					: undefined;

			return configs.map((c) => {
				const isThisRowType = c.configKey === configuration.configType;

				if (isThisRowType && dateTime) {
					return {
						label: `(${formatDateTimeAsCalendarDate(dateTime, false)})`,
						value: c.configKey,
					};
				}

				return {
					label: c.configKey,
					value: c.configKey,
				};
			});
		},
		[configs],
	);

	return (
		<Card className="mb-2">
			<h2>Starten</h2>

			<ConfigDropdown<StartContentConfigType>
				configOptions={options}
				onChange={(e) => {
					const selectedConfig = configs.find((c) => c.configKey === e.value);

					if (selectedConfig) {
						selectedConfig.addMutation({
							contentNodeId: treeNode.id,
							...selectedConfig.addMutationPayload,
						});
					}
				}}
				isPresentational={false}
			/>

			<ConfigPreview<ConfigurationGraphqlInterfaceWithDateTime<StartContentConfigType>>
				dataKey={"id"}
				selectedConfigs={treeNode.typeDefinition.startConfigs as any[]}
				template={(
					configuration: ConfigurationGraphqlInterfaceWithDateTime<StartContentConfigType>,
				) => {
					return (
						<>
							<ConfigItem<StartContentConfigType, StartContentConfigType>
								isPresentational={true}
								isLoading={isRemovingStartContentConfig}
								configType={configuration.configType}
								canEdit={
									configs.find((c) => c.configKey === configuration.configType)
										?.editable as boolean
								}
								configOptions={createPreviewOptions(configuration)}
								onDelete={() => {
									removeStartContentConfig({
										variables: {
											input: {
												contentNodeId: treeNode.id,
												configId: configuration.id,
											},
										},
									});
								}}
								editDialog={(props: any) => (
									<>
										<ProjectExtendStartContentConfigEditDialogs
											{...props}
											treeNodeFragmentRef={treeNode}
											// @ts-ignore
											configurationFragmentRef={configuration}
										/>
									</>
								)}
							/>
						</>
					);
				}}
			/>
		</Card>
	);
};
